import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Types whose value is chosen from a browser picker rather than typed.
 *
 * A native date field only opens its calendar when the small indicator icon is hit — clicking
 * the field itself does nothing, and `appearance-none` below makes that icon smaller still.
 * People click the field, nothing happens, and the control reads as broken. `showPicker()` opens
 * it from a click anywhere in the field, which is what everyone expects of a date box.
 */
const PICKER_TYPES = new Set(['date', 'time', 'datetime-local', 'month', 'week']);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type = 'text', onClick, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      onClick={(e) => {
        onClick?.(e);
        if (PICKER_TYPES.has(type) && !e.defaultPrevented) {
          // Not every browser has showPicker, and it throws unless the click is a real user
          // gesture. Either way the native icon still works, so a failure here changes nothing.
          try { (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); }
          catch { /* unsupported or not user-activated — the icon remains */ }
        }
      }}
      className={cn(
        // min-w-0 / max-w-full / appearance-none: a native date or time control carries an
        // INTRINSIC width and height from the browser's picker chrome, which beats w-full and
        // h-9. On the Add Judgment form that made "Judgment date" both wider and taller than
        // every field around it, and it ran past the card's right edge. Dropping the native
        // appearance lets it size like any other input; h-9 above then holds the height, which
        // is what collapses if appearance-none is applied without one.
        'flex h-9 w-full min-w-0 max-w-full appearance-none rounded-lg border border-border/70 bg-card px-3 py-2 text-sm text-foreground transition-all duration-200 placeholder:text-muted-foreground/50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50 focus:shadow-sm disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
